import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Close, Refresh, Search } from '@mui/icons-material';
import MroLabShell from './MroLabShell';
import MroLabWorkOrderDetail from './MroLabWorkOrderDetail';
import { useMroLabQueues } from './useMroLabQueues';
import {
  LAB_LANES,
  camoWorkOrderNumber,
  formatShortDate,
  mroReferenceNumber,
  pendingActionLabelForLane
} from './mroLabLanes';

function MroLabLanePage({ laneKey }) {
  const lane = LAB_LANES[laneKey];
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const { loading, error, counts, rowsByLane, refresh } = useMroLabQueues(search);
  const rows = rowsByLane[laneKey] || [];

  if (!lane) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Unknown lab lane.</Alert>
      </Box>
    );
  }

  return (
    <MroLabShell
      title={lane.title}
      subtitle={lane.subtitle}
      accent={lane.accent}
      counts={counts}
      actions={(
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={refresh}
          disabled={loading}
        >
          Refresh
        </Button>
      )}
    >
      <Stack spacing={2.5}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: lane.accentSoft
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="overline" sx={{ color: lane.accent, fontWeight: 700, letterSpacing: 1 }}>
                Pending in this lane
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.1, color: lane.accent }}>
                {loading ? '—' : counts[laneKey] ?? 0}
              </Typography>
            </Box>
            <TextField
              size="small"
              placeholder="Search job, aircraft, title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ bgcolor: '#fff', minWidth: { sm: 320 }, borderRadius: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={36} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700 }}>MRO job</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>CAMO WO</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Aircraft</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Scheduled</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Pending action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 6 }}>
                        <Stack alignItems="center" spacing={0.5}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Queue clear
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            No work orders are waiting in {lane.title} right now.
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((wo) => (
                      <TableRow
                        key={wo._id}
                        hover
                        sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                        onClick={() => setSelected(wo)}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'ui-monospace, monospace' }}>
                            {mroReferenceNumber(wo)}
                          </Typography>
                        </TableCell>
                        <TableCell>{camoWorkOrderNumber(wo)}</TableCell>
                        <TableCell>{wo.aircraftId?.registration || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography variant="body2" noWrap title={wo.title || ''}>
                            {wo.title || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatShortDate(wo.scheduledDate)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={wo.status || 'UNKNOWN'} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: lane.accent, fontWeight: 600 }}>
                            {pendingActionLabelForLane(laneKey, wo)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
            {selected ? `Work order detail · ${mroReferenceNumber(selected)}` : 'Work order'}
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={() => setSelected(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected ? <MroLabWorkOrderDetail workOrder={selected} /> : null}
        </DialogContent>
      </Dialog>
    </MroLabShell>
  );
}

export default MroLabLanePage;
